import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import Starfield from "@/components/Starfield";
import Wordmark, { StarGlyph } from "@/components/Wordmark";
import ShareCard from "@/components/ShareCard";
import { PrimaryCTA, SecondaryCTA } from "@/components/CosmicButton";
import { EMOTIONAL_DECODER } from "@/data/decoder";
import { BRAND, CHART_DECODER_URL, buildShareUrl } from "@/config";
import { track, EVENTS } from "@/lib/analytics";

const EASE = [0.16, 1, 0.3, 1];

const rise = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.15 },
    transition: { duration: 0.7, ease: EASE },
};

function SectionLabel({ n, children }) {
    return (
        <p className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-celestial-blue">
            <span className="text-celestial-gold">{n}</span>
            <span className="h-px w-8 bg-white/20" />
            {children}
        </p>
    );
}

export default function Result() {
    const { patternSlug } = useParams();
    const pattern = EMOTIONAL_DECODER.patterns.find(
        (p) => p.slug === patternSlug,
    );

    useEffect(() => {
        if (pattern) {
            document.title = `${pattern.title} · WHY THE FUCK DO I DO THAT?`;
            track(EVENTS.RESULT_VIEWED, {
                decoder: EMOTIONAL_DECODER.id,
                pattern: pattern.key,
            });
        }
    }, [pattern]);

    if (!pattern) return <Navigate to="/" replace />;

    const share = async () => {
        const text = `I got "${pattern.title}" on "${BRAND.quizTitle}" — ${pattern.shareQuote}`;
        const url = buildShareUrl(pattern.slug);
        try {
            if (navigator.share) {
                await navigator.share({ title: BRAND.quizTitle, text, url });
                track(EVENTS.RESULT_SHARED, { pattern: pattern.key, method: "native" });
                toast.success("Sent. Let's see what they get.");
            } else {
                await navigator.clipboard.writeText(`${text}\n${url}`);
                track(EVENTS.RESULT_SHARED, { pattern: pattern.key, method: "clipboard" });
                toast.success("Result copied. Go paste it on someone.");
            }
        } catch (err) {
            if (!err || err.name !== "AbortError") {
                toast.error("Couldn't share — copy the URL up top instead.");
            }
        }
    };

    const onDecodeChart = () => {
        track(EVENTS.DECODE_CHART_CLICKED, { pattern: pattern.key });
        if (CHART_DECODER_URL) {
            window.open(CHART_DECODER_URL, "_blank", "noopener,noreferrer");
        } else {
            toast("The chart decoder isn't wired up yet.", {
                description: "The destination URL will be set before launch.",
            });
        }
    };

    return (
        <main className="relative min-h-dvh overflow-hidden bg-ink text-slate-200">
            <Starfield density={70} />
            <div className="relative z-10 mx-auto w-full max-w-2xl px-5 pb-16 sm:px-8">
                <header className="flex items-center justify-between py-7">
                    <Wordmark />
                    <StarGlyph className="h-4 w-4 text-celestial-gold" />
                </header>

                <motion.section
                    {...rise}
                    data-testid="result-header"
                    className="pb-14 pt-8"
                >
                    <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-celestial-blue">
                        {EMOTIONAL_DECODER.resultIntro}
                    </p>
                    <h1
                        data-testid="result-title"
                        className="mt-4 font-display text-[clamp(2.4rem,10vw,4.2rem)] leading-[1.05] text-celestial-gold"
                    >
                        {pattern.title}
                    </h1>
                </motion.section>

                <motion.section
                    {...rise}
                    data-testid="result-recognition"
                    className="border-t border-white/10 py-12"
                >
                    <SectionLabel n="01">Recognition</SectionLabel>
                    <p className="mt-5 whitespace-pre-line text-[17px] leading-relaxed text-slate-300">
                        {pattern.recognition}
                    </p>
                </motion.section>

                <motion.section
                    {...rise}
                    data-testid="result-underneath"
                    className="border-t border-white/10 py-12"
                >
                    <SectionLabel n="02">What might be underneath it</SectionLabel>
                    <p className="mt-5 whitespace-pre-line text-[17px] leading-relaxed text-slate-300">
                        {pattern.underneath}
                    </p>
                </motion.section>

                <motion.section
                    {...rise}
                    data-testid="result-reveal"
                    className="border-t border-white/10 py-12"
                >
                    <SectionLabel n="03">Reveal</SectionLabel>
                    <p className="mt-6 whitespace-pre-line border-l-2 border-celestial-gold/50 pl-6 font-display text-2xl italic leading-snug text-celestial-gold sm:text-[1.7rem]">
                        {pattern.reveal}
                    </p>
                </motion.section>

                <motion.section
                    {...rise}
                    data-testid="result-chart"
                    className="border-t border-white/10 py-12"
                >
                    <SectionLabel n="04">Where to look in your chart</SectionLabel>
                    <div className="mt-6 divide-y divide-white/10">
                        {pattern.chartAreas.map((area) => (
                            <div
                                key={area.name}
                                data-testid={`result-chart-area-${area.name.toLowerCase()}`}
                                className="flex gap-5 py-6 first:pt-1"
                            >
                                <p className="w-24 shrink-0 font-display text-2xl italic text-celestial-blue">
                                    {area.name}
                                </p>
                                <p className="text-[15px] leading-relaxed text-slate-300">
                                    {area.blurb}
                                </p>
                            </div>
                        ))}
                    </div>
                    <p
                        data-testid="result-chart-disclaimer"
                        className="mt-8 flex gap-3 rounded-2xl border border-celestial-gold/25 bg-celestial-gold/5 p-5 text-sm leading-relaxed text-slate-300"
                    >
                        <StarGlyph className="mt-0.5 h-4 w-4 shrink-0 text-celestial-gold" />
                        <span>
                            Your answers don't tell us what placements you
                            have. They point toward parts of your chart that
                            may be worth exploring.
                        </span>
                    </p>
                </motion.section>

                <motion.section
                    {...rise}
                    data-testid="result-chart-cta"
                    className="border-t border-white/10 py-14 text-center"
                >
                    <h2 className="font-sans text-2xl font-bold uppercase leading-snug tracking-tight sm:text-3xl">
                        YOU FOUND THE PATTERN.{" "}
                        <span className="text-slate-400">
                            NOW LET'S FIND OUT HOW IT SHOWS UP IN YOUR CHART.
                        </span>
                    </h2>
                    <p className="mx-auto mt-5 max-w-md text-[15px] leading-relaxed text-slate-400">
                        Your answers showed us what you tend to do. Your birth
                        chart can add another layer to how you seek emotional
                        safety, handle uncertainty, communicate your needs, and
                        respond when connection feels vulnerable.
                    </p>
                    <div className="mt-9">
                        <PrimaryCTA
                            onClick={onDecodeChart}
                            testid="result-decode-chart-cta"
                        >
                            Decode my chart
                        </PrimaryCTA>
                    </div>
                </motion.section>

                <motion.section
                    {...rise}
                    data-testid="result-share"
                    className="border-t border-white/10 py-12"
                >
                    <SectionLabel n="05">Send it to someone</SectionLabel>
                    <div className="mt-8">
                        <ShareCard pattern={pattern} />
                    </div>
                    <div className="mt-6">
                        <SecondaryCTA
                            onClick={share}
                            testid="result-share-button"
                            className="w-full sm:w-auto"
                        >
                            Share my result
                        </SecondaryCTA>
                    </div>
                </motion.section>

                <footer className="border-t border-white/10 pt-8 text-center">
                    <Link
                        to="/quiz"
                        data-testid="result-retake-link"
                        className="text-sm text-slate-500 transition-colors hover:text-celestial-gold"
                    >
                        Run the decoder again →
                    </Link>
                    <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-600">
                        Astrology is a lens, not a diagnosis.
                    </p>
                </footer>
            </div>
        </main>
    );
}
